import { WORLD_PRIMITIVES } from './constants';
import type {
  MotionEditorWorldAssetFormat,
  MotionEditorWorldAssetObject,
  MotionEditorWorldObject,
  MotionEditorWorldObjectTransform,
  MotionEditorWorldPrimitiveObject,
  MotionEditorWorldPrimitiveType,
  MotionEditorWorldVector3Tuple,
} from './types';

export function createMotionEditorWorldPrimitive(
  type: MotionEditorWorldPrimitiveType,
  point: MotionEditorWorldVector3Tuple,
  existingObjects: readonly MotionEditorWorldObject[],
): MotionEditorWorldPrimitiveObject {
  const primitive = WORLD_PRIMITIVES[type] ?? WORLD_PRIMITIVES.cube;
  return {
    id: createWorldObjectId(),
    name: nextWorldObjectName(primitive.defaultName, existingObjects),
    kind: 'primitive',
    primitiveType: type,
    transform: {
      position: [point[0], primitive.y, point[2]],
      rotation: [0, 0, 0],
      scale: primitive.scale,
    },
    visible: true,
    locked: false,
  };
}

export function createMotionEditorWorldAsset(
  file: File,
  format: MotionEditorWorldAssetFormat,
  point: MotionEditorWorldVector3Tuple,
  existingObjects: readonly MotionEditorWorldObject[],
): MotionEditorWorldAssetObject {
  const baseName = file.name.replace(/\.[^.]+$/, '') || 'Asset';
  return {
    id: createWorldObjectId(),
    name: nextWorldObjectName(baseName, existingObjects),
    kind: 'asset',
    assetFormat: format,
    assetFile: file,
    assetUrl: URL.createObjectURL(file),
    fileName: file.name,
    transform: defaultWorldObjectTransform(point),
    visible: true,
    locked: false,
  };
}

export function revokeMotionEditorWorldObjectAssets(
  objects: readonly MotionEditorWorldObject[],
) {
  for (const object of objects) {
    if (object.kind === 'asset' && object.assetUrl.startsWith('blob:')) {
      URL.revokeObjectURL(object.assetUrl);
    }
  }
}

export function getWorldPrimitiveLabel(type: MotionEditorWorldPrimitiveType) {
  return WORLD_PRIMITIVES[type]?.label ?? type;
}

function defaultWorldObjectTransform(
  point: MotionEditorWorldVector3Tuple,
): MotionEditorWorldObjectTransform {
  return {
    position: point,
    rotation: [0, 0, 0],
    scale: [1, 1, 1],
  };
}

function nextWorldObjectName(
  baseName: string,
  existingObjects: readonly MotionEditorWorldObject[],
) {
  const count =
    existingObjects.filter((object) => object.name.startsWith(baseName))
      .length + 1;
  return `${baseName} ${count}`;
}

function createWorldObjectId() {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `world-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}
